/**
 * Application Configuration
 * Reads from environment variables with sensible defaults
 */

import { BrandTheme } from '@dynamic-flow/types';

export interface AppConfig {
  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
  };

  // Form URLs
  forms: {
    publicBaseUrl: string;
    previewBaseUrl: string;
    embedBaseUrl: string;
  };

  // App Settings
  app: {
    name: string;
    version: string;
    environment: 'development' | 'staging' | 'production';
    debug: boolean;
  };

  // Feature Flags
  features: {
    analytics: boolean;
    themes: boolean;
    logicBuilder: boolean;
    draftTracking: boolean;
    autoSave: boolean;
  };

  // UI Configuration
  ui: {
    defaultTheme: BrandTheme;
    maxFlowsPerPage: number;
    autoSaveInterval: number;
  };
}

// Environment variable helpers
const getEnvVar = (key: string, defaultValue: string): BrandTheme => {
  return import.meta.env[key] || defaultValue;
};

const getEnvBool = (key: string, defaultValue: boolean): boolean => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
};

const getEnvNumber = (key: string, defaultValue: number): number => {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Configuration object
export const appConfig: AppConfig = {
  api: {
    baseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:3001/api'),
    timeout: getEnvNumber('VITE_API_TIMEOUT', 10000),
  },
  forms: {
    publicBaseUrl: getEnvVar('VITE_FORM_PUBLIC_URL', 'http://localhost:3003'),
    previewBaseUrl: getEnvVar(
      'VITE_FORM_PREVIEW_URL',
      'http://localhost:3003/preview'
    ),
    embedBaseUrl: getEnvVar(
      'VITE_FORM_EMBED_URL',
      'http://localhost:3003/embed'
    ),
  },
  app: {
    name: getEnvVar('VITE_APP_NAME', 'Dynamic Flow Builder'),
    version: getEnvVar('VITE_APP_VERSION', '1.0.0'),
    environment: getEnvVar('VITE_APP_ENV', 'development') as
      | 'development'
      | 'staging'
      | 'production',
    debug: getEnvBool('VITE_APP_DEBUG', true),
  },
  features: {
    analytics: getEnvBool('VITE_FEATURE_ANALYTICS', true),
    themes: getEnvBool('VITE_FEATURE_THEMES', true),
    logicBuilder: getEnvBool('VITE_FEATURE_LOGIC_BUILDER', true),
    draftTracking: getEnvBool('VITE_FEATURE_DRAFT_TRACKING', true),
    autoSave: getEnvBool('VITE_FEATURE_AUTO_SAVE', false),
  },
  ui: {
    defaultTheme: getEnvVar('VITE_UI_DEFAULT_THEME', 'default'),
    maxFlowsPerPage: getEnvNumber('VITE_UI_MAX_FLOWS_PER_PAGE', 20),
    autoSaveInterval: getEnvNumber('VITE_UI_AUTO_SAVE_INTERVAL', 30000),
  },
};

// Helper functions
export const getFormUrl = (
  flowId: string,
  type: 'public' | 'preview' | 'embed' = 'public'
): string => {
  const baseUrl = appConfig.forms[`${type}BaseUrl`];
  return `${baseUrl}/${flowId}`;
};

export const EVENT_ID_QUERY_PARAM = 'eventId';

export function getEventFormUrl(url: string, eventId: string): string {
  if (!url || !eventId) {
    return url;
  }
  const parsed = new URL(url);
  if (!parsed.searchParams.has(EVENT_ID_QUERY_PARAM)) {
    parsed.searchParams.set(EVENT_ID_QUERY_PARAM, eventId);
  }
  return parsed.toString();
}

export const getApiUrl = (endpoint: string): string => {
  return `${appConfig.api.baseUrl}${endpoint}`;
};

export const isProduction = (): boolean => {
  return appConfig.app.environment === 'production';
};

export const isDevelopment = (): boolean => {
  return appConfig.app.environment === 'development';
};

export const isStaging = (): boolean => {
  return appConfig.app.environment === 'staging';
};

// Log configuration in development
if (appConfig.app.debug) {
  console.log('🔧 App Configuration:', appConfig);
}
